import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

import {
  FAB_DRAG_THRESHOLD_PX,
  FAB_MARGIN_PX,
  FAB_SIZE_PX,
} from "../constants";
import { localGet, localSet, safeJsonParse } from "../utils/safe-storage";

export interface FabPosition {
  x: number;
  y: number;
}

type FabSide = "left" | "right";

interface StoredFab extends FabPosition {
  side?: FabSide;
}

interface UseDraggableFabArgs {
  storageKey: string;
  size?: number;
  margin?: number;
  /** Snap to the nearest left/right edge when a drag ends. */
  snapToEdge?: boolean;
}

interface UseDraggableFabResult {
  position: FabPosition;
  dragging: boolean;
  onPointerDown: (e: ReactPointerEvent) => void;
  /**
   * Call from `onClickCapture`. Returns true if the click was the tail of a
   * drag and should be swallowed (so Maya doesn't open on drop).
   */
  consumeDragClick: () => boolean;
}

interface DragState {
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  moved: boolean;
}

const viewport = () => ({
  w: typeof window !== "undefined" ? window.innerWidth : 1024,
  h: typeof window !== "undefined" ? window.innerHeight : 768,
});

const clampPos = (
  pos: FabPosition,
  size: number,
  margin: number,
): FabPosition => {
  const { w, h } = viewport();
  const maxX = Math.max(margin, w - size - margin);
  const maxY = Math.max(margin, h - size - margin);
  return {
    x: Math.min(Math.max(pos.x, margin), maxX),
    y: Math.min(Math.max(pos.y, margin), maxY),
  };
};

/** x coordinate that pins the FAB flush against the given edge. */
const edgeX = (side: FabSide, size: number, margin: number): number => {
  const { w } = viewport();
  return side === "right" ? Math.max(margin, w - size - margin) : margin;
};

/** Resolve the starting position + edge synchronously from storage or default. */
const initialState = (
  storageKey: string,
  size: number,
  margin: number,
): { pos: FabPosition; side: FabSide } => {
  const { w, h } = viewport();
  const stored = safeJsonParse<StoredFab | null>(localGet(storageKey), null);
  if (stored && typeof stored.x === "number" && typeof stored.y === "number") {
    const side: FabSide =
      stored.side ?? (stored.x + size / 2 >= w / 2 ? "right" : "left");
    // Re-pin x to the remembered edge so the FAB is flush on this viewport.
    return {
      pos: clampPos({ x: edgeX(side, size, margin), y: stored.y }, size, margin),
      side,
    };
  }
  // Default to the trailing edge for the document's direction — bottom-right in
  // LTR, bottom-left in RTL — so the FAB starts where the eye ends up.
  const rtl =
    typeof document !== "undefined" && document.documentElement.dir === "rtl";
  const side: FabSide = rtl ? "left" : "right";
  return {
    pos: { x: edgeX(side, size, margin), y: h - size - margin },
    side,
  };
};

export const useDraggableFab = ({
  storageKey,
  size = FAB_SIZE_PX,
  margin = FAB_MARGIN_PX,
  snapToEdge = true,
}: UseDraggableFabArgs): UseDraggableFabResult => {
  const sideRef = useRef<FabSide>("right");
  const [position, setPosition] = useState<FabPosition>(() => {
    const init = initialState(storageKey, size, margin);
    sideRef.current = init.side;
    return init.pos;
  });
  const [dragging, setDragging] = useState(false);

  const dragRef = useRef<DragState | null>(null);
  const suppressClickRef = useRef(false);

  const persist = useCallback(
    (pos: FabPosition) =>
      localSet(storageKey, JSON.stringify({ ...pos, side: sideRef.current })),
    [storageKey],
  );

  // On viewport resize, re-pin the FAB to its remembered edge (and clamp y), so
  // it follows the edge whether the window grows or shrinks.
  useEffect(() => {
    const onResize = () =>
      setPosition((prev) => {
        const x = snapToEdge ? edgeX(sideRef.current, size, margin) : prev.x;
        const next = clampPos({ x, y: prev.y }, size, margin);
        persist(next);
        return next;
      });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [persist, size, margin, snapToEdge]);

  // When the FAB is resized, re-pin to its edge and clamp so it stays flush and
  // fully on-screen at the new diameter.
  useEffect(() => {
    setPosition((prev) => {
      const x = snapToEdge ? edgeX(sideRef.current, size, margin) : prev.x;
      const next = clampPos({ x, y: prev.y }, size, margin);
      persist(next);
      return next;
    });
  }, [size, margin, snapToEdge, persist]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (e.button !== 0) return; // primary / touch only
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        originX: position.x,
        originY: position.y,
        moved: false,
      };

      const handleMove = (ev: PointerEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        const dx = ev.clientX - drag.startX;
        const dy = ev.clientY - drag.startY;
        if (!drag.moved && Math.hypot(dx, dy) < FAB_DRAG_THRESHOLD_PX) return;
        drag.moved = true;
        setDragging(true);
        ev.preventDefault();
        setPosition(
          clampPos({ x: drag.originX + dx, y: drag.originY + dy }, size, margin),
        );
      };

      const handleUp = () => {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleUp);
        const drag = dragRef.current;
        dragRef.current = null;
        setDragging(false);
        if (!drag?.moved) return; // a tap — let the click open Maya
        suppressClickRef.current = true;
        setPosition((prev) => {
          const { w } = viewport();
          const side: FabSide = prev.x + size / 2 < w / 2 ? "left" : "right";
          sideRef.current = side;
          const x = snapToEdge ? edgeX(side, size, margin) : prev.x;
          const next = clampPos({ x, y: prev.y }, size, margin);
          persist(next);
          return next;
        });
      };

      window.addEventListener("pointermove", handleMove, { passive: false });
      window.addEventListener("pointerup", handleUp);
    },
    [position.x, position.y, persist, margin, size, snapToEdge],
  );

  const consumeDragClick = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }, []);

  return { position, dragging, onPointerDown, consumeDragClick };
};
